import { Component, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { Router } from '@angular/router'
import { LocalDate } from '@lemon/core/model/common'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutTilintarkastajaService } from 'app/_angular/service/kirjautunut-tilintarkastaja.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { Tilintarkastaja } from 'app/_jaettu-lemonator/model/tilintarkastaja'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { TilintarkastajaUriService } from 'app/_jaettu-lemonator/service/tilintarkastaja-uri.service'
import { TilintarkastajaService } from 'app/_jaettu-lemonator/service/tilintarkastaja.service'

interface AsiakasOikeus {
  asiakasAvain: string
  asiakasNimi: string
  ytunnus: string
  tilikausiLoppuu: LocalDate
}

@Component({
  standalone: false,
  templateUrl: './tilintarkastaja-aloitus.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../../login/login.component.css', './tilintarkastaja-aloitus.component.css'],
})
export class TilintarkastajaAloitusComponent implements OnInit {
  readonly tilintarkastaja = signal<Tilintarkastaja | null>(null)
  readonly oikeudet = signal<AsiakasOikeus[]>([])
  readonly ladataan = signal(true)
  year = new Date().getFullYear()

  constructor(
    private _kirjautunutTilintarkastajaService: KirjautunutTilintarkastajaService,
    private _firebaseLemonator: FirebaseLemonator,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _tilintarkastajaService: TilintarkastajaService,
    private _tilintarkastajaUriService: TilintarkastajaUriService,
    private _asiakasUriService: AsiakasUriService,
    private _router: Router,
  ) {}

  async ngOnInit() {
    const tilintarkastajaTiedot = this._kirjautunutTilintarkastajaService.tilintarkastaja

    if (!tilintarkastajaTiedot) {
      this._router.navigate(['/tilintarkastajat/kirjaudu'])
      return
    }

    try {
      const tilintarkastaja = await this._firebaseLemonator.firestoreDoc<Tilintarkastaja>(this._tilintarkastajaUriService.annaTilintarkastajaUri(tilintarkastajaTiedot.tilintarkastajaAvain)).get()
      this.tilintarkastaja.set(tilintarkastaja)

      if (!tilintarkastaja) {
        console.error('Tilintarkastajaa ei löytynyt')
        this._router.navigate(['/tilintarkastajat/kirjaudu'])
        return
      }

      const oikeudetPromises: Promise<AsiakasOikeus | null>[] = []

      if (tilintarkastaja.asiakasOikeudet) {
        for (const [asiakasAvain] of Object.entries(tilintarkastaja.asiakasOikeudet)) {
          if (this._tilintarkastajaService.onkoOikeusVoimassa(tilintarkastaja, asiakasAvain)) {
            oikeudetPromises.push(this.haeAsiakkaanTiedot(asiakasAvain))
          }
        }
      }

      const oikeudetTaulukko = await Promise.all(oikeudetPromises)
      const oikeudet = oikeudetTaulukko.filter(o => o !== null) as AsiakasOikeus[]
      // Järjestä asiakkaan nimen mukaan
      this.oikeudet.set(oikeudet.sort((a, b) => a.asiakasNimi.localeCompare(b.asiakasNimi, 'fi'))) // asc
    } catch (error) {
      console.error('Virhe tilintarkastajan tietojen haussa:', error)
    } finally {
      this.ladataan.set(false)
    }
  }

  private async haeAsiakkaanTiedot(asiakasAvain: string): Promise<AsiakasOikeus | null> {
    try {
      const asiakas = await this._firebaseLemonator.firestoreDoc<Asiakas>(this._asiakasUriService.annaAsiakasUri(asiakasAvain)).get()

      if (!asiakas) {
        console.warn('Asiakasta ei löytynyt:', asiakasAvain)
        return null
      }

      const tilikausi = this._asiakasJaettuService.annaViimeisinSuljettuTilikausi(asiakas) ?? this._asiakasJaettuService.annaViimeinenTilikausi(asiakas)

      return {
        asiakasAvain,
        asiakasNimi: asiakas.nimi,
        ytunnus: asiakas.ytunnus,
        tilikausiLoppuu: tilikausi?.loppuu ?? null,
      }
    } catch (error) {
      console.error('Virhe asiakkaan tietojen haussa:', error)
      return null
    }
  }

  avaaAsiakas(oikeus: AsiakasOikeus) {
    if (oikeus.tilikausiLoppuu) {
      this._router.navigate([`asiakkaat/${oikeus.asiakasAvain}/tilintarkastus/${oikeus.tilikausiLoppuu.year}/${oikeus.tilikausiLoppuu.month}`])
    } else {
      this._router.navigate([`asiakkaat/${oikeus.asiakasAvain}/tilintarkastus`])
    }
  }

  async kirjauduUlos() {
    await this._kirjautunutTilintarkastajaService.kirjauduUlos()
  }
}
